import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import { ModalId } from "../../../enums/modalId";
import { getFilingInfoRequired } from "../../../utils/getFilingInfoRequired";
import {
  FilingInfoSavePayload,
  formatFilingDetail,
  isFilingInfoSaveEnabled,
} from "./filingInfoModal.utils";
import FilingInfoModalBody from "./filingInfoModalBody";

/**
 * Props for {@link FilingInfoModal}. Auth-status ids mirror the CPT row so
 * the modal can apply the same {@link getFilingInfoRequired} rules as the table.
 */
interface FilingInfoModalProps {
  /** Dismisses the dialog without persisting changes. */
  onClose: () => void;
  /** Filing mode already saved on the row, used to preselect the radio group. */
  initialFilingMode?: string;
  /** Portal name or fax number already saved on the row. */
  initialFilingDetail?: string;
  /**
   * PA-required status before the transition that unlocked Filing Info
   * (`filingInfo.priorAuthStatusId` on the CPT row).
   */
  oldAuthStatusId?: string;
  /** Current PA-required status on the row (`paRequired.currentStatusId`). */
  newAuthStatusId?: string;
  /** Called with the current filing mode and detail when Save is clicked. */
  onSave?: (payload: FilingInfoSavePayload) => void;
}

/**
 * Filing Info dialog for choosing how a J-code / CPT code is filed.
 *
 * Owns `filingMode` and `filingDetail` so Save (and other callers) can read
 * the current selection from this parent instead of the modal body. Save is
 * disabled when {@link getFilingInfoRequired} returns false for the passed
 * auth-status pair, or when the mode/detail pair fails
 * {@link isFilingInfoSaveEnabled}. The dialog mounts per open, so the initial
 * props seed the first render only.
 *
 * @param props - Close handler, saved initial selection, auth statuses, and save emitter
 * @returns Filing Info modal shell with controlled form body
 */
const FilingInfoModal = (props: FilingInfoModalProps) => {
  const [filingMode, setFilingMode] = useState(props.initialFilingMode ?? "");
  const [filingDetail, setFilingDetail] = useState(
    props.initialFilingDetail ?? "",
  );
  /** Mirrors the CPT table cell: filing must be captured for this auth transition. */
  const isFilingInfoRequired = getFilingInfoRequired(
    props.oldAuthStatusId,
    props.newAuthStatusId,
  );

  /**
   * Updates the filing mode and clears the generic detail so a leftover
   * portal name is not treated as a fax number (or vice versa).
   *
   * @param nextFilingMode - Newly selected filing-mode value
   */
  const handleFilingModeChange = (nextFilingMode: string) => {
    setFilingMode(nextFilingMode);
    setFilingDetail("");
  };

  /**
   * Persists the current filing mode and detail via {@link FilingInfoModalProps.onSave},
   * then closes the dialog. Fax details are emitted as `+1-XXX-XXX-XXXX`,
   * including when the user leaves the seeded value untouched.
   */
  const handleSave = () => {
    props.onSave?.({
      filingMode,
      filingDetail: formatFilingDetail(filingMode, filingDetail),
    });
    props.onClose();
  };

  return (
    <Modal
      dialogId={ModalId.FILING_INFO_MODAL}
      onSave={handleSave}
      disableSave={
        !isFilingInfoSaveEnabled(filingMode, filingDetail) ||
        !isFilingInfoRequired
      }
      title={"Filing Info"}
      saveButtonText={"Save"}
      cancelText={"Cancel"}
      onCancel={props?.onClose}
      onClose={props?.onClose}
      maxHeightPercentage={50}
    >
      <FilingInfoModalBody
        filingMode={filingMode}
        filingDetail={filingDetail}
        onFilingModeChange={handleFilingModeChange}
        onFilingDetailChange={setFilingDetail}
      />
    </Modal>
  );
};

export default FilingInfoModal;
